// Simplified UnmappedMeasurements with toggles and map popup
import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { GoogleMap, PolygonF, MarkerF, useLoadScript } from '@react-google-maps/api';
import { supabase } from '../../supabaseClient';
import Layout from '../Layout';
import { AuthContext } from '../../context/AuthContext';
import { CheckCircleIcon, ExclamationCircleIcon, ArrowPathIcon, BoltIcon, ArrowsPointingOutIcon, PlusIcon, MapIcon, XMarkIcon, EyeIcon } from '@heroicons/react/20/solid';
import { MapPinIcon } from '@heroicons/react/24/solid';
import SmartMapModal from './SmartMapModal';

// Helper functions
function haversineDistance(coord1, coord2) {
  const R = 6371000;
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLon = (coord2.lng - coord1.lng) * Math.PI / 180;
  const lat1 = coord1.lat * Math.PI / 180;
  const lat2 = coord2.lat * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function isPointInPolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].lng, yi = polygon[i].lat;
    const xj = polygon[j].lng, yj = polygon[j].lat;
    const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
                      (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi + 0.0000001) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

function calculateDistanceToPolygon(point, polygon) {
  let minDistance = Infinity;
  
  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const distance = distanceToLineSegment(point, polygon[i], polygon[j]);
    minDistance = Math.min(minDistance, distance);
  }
  
  return minDistance;
}

function distanceToLineSegment(point, lineStart, lineEnd) {
  const A = point.lng - lineStart.lng;
  const B = point.lat - lineStart.lat;
  const C = lineEnd.lng - lineStart.lng;
  const D = lineEnd.lat - lineStart.lat;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  
  if (lenSq === 0) {
    return haversineDistance(point, lineStart);
  }
  
  const param = dot / lenSq;
  let closestPoint;
  
  if (param < 0) {
    closestPoint = lineStart;
  } else if (param > 1) {
    closestPoint = lineEnd;
  } else {
    closestPoint = {
      lat: lineStart.lat + param * D,
      lng: lineStart.lng + param * C
    };
  }
  
  return haversineDistance(point, closestPoint);
}

function calculateDistanceToProject(measurementCoord, project) {
  if (project.parsedPolygon && project.parsedPolygon.length > 0) {
    if (isPointInPolygon(measurementCoord, project.parsedPolygon)) {
      return 0;
    }
    return calculateDistanceToPolygon(measurementCoord, project.parsedPolygon);
  }
  
  if (project.centerCoord) {
    return haversineDistance(measurementCoord, project.centerCoord);
  }
  
  return Infinity;
}

// Map Popup Component
function MapPopup({ isOpen, onClose, measurements, projects, title, focusedMeasurement = null }) {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw',
  });

  const getMapCenter = () => {
    if (focusedMeasurement) {
      return focusedMeasurement.coords;
    }
    
    if (measurements.length === 0) return { lat: 37.0902, lng: -95.7129 };
    
    const avgLat = measurements.reduce((sum, m) => sum + m.coords.lat, 0) / measurements.length;
    const avgLng = measurements.reduce((sum, m) => sum + m.coords.lng, 0) / measurements.length;
    return { lat: avgLat, lng: avgLng };
  };

  const getMapZoom = () => {
    return focusedMeasurement ? 16 : 12;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {focusedMeasurement 
              ? `Focused: ${focusedMeasurement.projectdescription || `Measurement ${focusedMeasurement.id}`}`
              : title
            }
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <div className="h-96">
          {isLoaded && (
            <GoogleMap
              center={getMapCenter()}
              zoom={getMapZoom()}
              mapContainerStyle={{ width: '100%', height: '100%' }}
              options={{ mapTypeId: 'hybrid' }}
            >
              {/* Project polygons for nearby measurements */}
              {projects.map(project => (
                project.parsedPolygon && (
                  <PolygonF
                    key={project.id}
                    path={project.parsedPolygon}
                    options={{
                      fillColor: '#3B82F6',
                      fillOpacity: 0.2,
                      strokeColor: '#1E40AF',
                      strokeWeight: 2,
                    }}
                  />
                )
              ))}
              
              {/* Project center markers */}
              {projects.map(project => (
                project.centerCoord && (
                  <MarkerF
                    key={`center_${project.id}`}
                    position={project.centerCoord}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      fillColor: '#3B82F6',
                      fillOpacity: 0.8,
                      strokeColor: '#1E40AF',
                      strokeWeight: 2,
                      scale: 8,
                    }}
                    title={project.name}
                  />
                )
              ))}
              
              {/* Measurement markers */}
              {measurements.map((measurement) => {
                const isFocused = focusedMeasurement && measurement.id === focusedMeasurement.id;
                return (
                  <MarkerF
                    key={measurement.id}
                    position={measurement.coords}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      fillColor: isFocused ? '#FFD700' : (measurement.closestProject ? '#10B981' : '#EF4444'),
                      fillOpacity: 1,
                      strokeColor: isFocused ? '#FF8C00' : '#FFFFFF',
                      strokeWeight: isFocused ? 3 : 2,
                      scale: isFocused ? 10 : 6,
                    }}
                    title={measurement.projectdescription || `Measurement ${measurement.id}`}
                  />
                );
              })}
            </GoogleMap>
          )}
        </div>
        
        <div className="p-4 bg-gray-50 text-sm text-gray-600">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span>Existing Projects</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Near Projects</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Isolated</span>
            </div>
            {focusedMeasurement && (
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-yellow-500 rounded"></div>
                <span>Focused Measurement</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UnmappedProject() {
  const { user } = useContext(AuthContext);
  
  // State
  const [allUnmappedMeasurements, setAllUnmappedMeasurements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMeasurements, setLoadingMeasurements] = useState(true);
  const [smartMapModalOpen, setSmartMapModalOpen] = useState(false);
  const [newProjectModal, setNewProjectModal] = useState({ isOpen: false, measurement: null });
  const [newProjectName, setNewProjectName] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  
  // Server-side clustering data
  const [serverClusters, setServerClusters] = useState([]);
  const [loadingClusters, setLoadingClusters] = useState(true);

  // View toggles
  const [viewMode, setViewMode] = useState('nearby'); // 'nearby' or 'isolated'
  const [mapPopupOpen, setMapPopupOpen] = useState(false);
  const [selectedMeasurements, setSelectedMeasurements] = useState(new Set());
  const [focusedMeasurement, setFocusedMeasurement] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const measurementsPerPage = 15;

  // Process measurements by proximity to projects
  const { nearbyMeasurements, isolatedMeasurements, clusterCount, clusteredCount, nearbyByProject } = useMemo(() => {
    if (!allUnmappedMeasurements.length) {
      return { 
        nearbyMeasurements: [], 
        isolatedMeasurements: [], 
        clusterCount: 0, 
        clusteredCount: 0,
        nearbyByProject: new Map()
      };
    }
    
    // Get all measurement IDs that are in server clusters
    const clusteredMeasurementIds = new Set();
    let totalClustered = 0;
    
    serverClusters.forEach(cluster => {
      cluster.measurements.forEach(measurement => {
        clusteredMeasurementIds.add(measurement.id);
        totalClustered++;
      });
    });
    
    // Filter out clustered measurements to get individuals
    const individualMeasurements = allUnmappedMeasurements
      .filter(measurement => !clusteredMeasurementIds.has(measurement.id))
      .map(measurement => {
        const coords = measurement.GpsCoordinates.split(',').map(Number);
        const measCoord = { lat: coords[0], lng: coords[1] };
        
        let closestProject = null;
        let closestDistance = Infinity;
        
        projects.forEach(project => {
          const distance = calculateDistanceToProject(measCoord, project);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestProject = project;
          }
        });
        
        return {
          ...measurement,
          coords: measCoord,
          closestProject,
          distanceToClosest: closestDistance === Infinity ? null : closestDistance
        };
      });
    
    // Separate by proximity (3km threshold)
    const nearby = individualMeasurements.filter(m => 
      m.distanceToClosest !== null && m.distanceToClosest <= 3000
    );
    const isolated = individualMeasurements.filter(m => 
      m.distanceToClosest === null || m.distanceToClosest > 3000
    );
    
    // Group nearby measurements by project for bulk actions
    const groupedByProject = new Map();
    nearby.forEach(measurement => {
      if (measurement.closestProject) {
        const projectId = measurement.closestProject.id;
        if (!groupedByProject.has(projectId)) {
          groupedByProject.set(projectId, {
            project: measurement.closestProject,
            measurements: []
          });
        }
        groupedByProject.get(projectId).measurements.push(measurement);
      }
    });
    
    return { 
      nearbyMeasurements: nearby,
      isolatedMeasurements: isolated,
      clusterCount: serverClusters.length,
      clusteredCount: totalClustered,
      nearbyByProject: groupedByProject
    };
  }, [allUnmappedMeasurements, projects, serverClusters]);

  // Current measurements based on view mode
  const currentMeasurements = viewMode === 'nearby' ? nearbyMeasurements : isolatedMeasurements;
  
  // Pagination
  const totalPages = Math.ceil(currentMeasurements.length / measurementsPerPage);
  const startIdx = (currentPage - 1) * measurementsPerPage;
  const paginatedMeasurements = currentMeasurements.slice(startIdx, startIdx + measurementsPerPage);

  const paginate = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // Reset pagination and selections when view mode changes
  useEffect(() => {
    setCurrentPage(1);
    setSelectedMeasurements(new Set());
  }, [viewMode]);

  // Fetch server-side clusters
  useEffect(() => {
    const fetchServerClusters = async () => {
      if (!allUnmappedMeasurements.length) {
        setLoadingClusters(false);
        return;
      }
      
      try {
        setLoadingClusters(true);
        const { data: clusterData, error } = await supabase.rpc('smartmap_clustered_geofences');
        
        if (error) {
          console.error('Smart clustering error:', error);
          setServerClusters([]);
          return;
        }
        
        if (!clusterData?.length) {
          setServerClusters([]);
          return;
        }
        
        const parsed = clusterData.map((c, idx) => {
          const polygon = c.polygon.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
          const inPolygon = allUnmappedMeasurements.filter(m => {
            const gps = m.GpsCoordinates?.split(',').map(Number);
            if (!gps) return false;
            return isPointInPolygon({ lat: gps[0], lng: gps[1] }, polygon);
          });
          
          return {
            id: `Cluster ${idx + 1}`,
            polygon,
            measurements: inPolygon,
            point_count: c.point_count,
          };
        });
        
        setServerClusters(parsed);
      } catch (error) {
        console.error('Error fetching server clusters:', error);
        setServerClusters([]);
      } finally {
        setLoadingClusters(false);
      }
    };
    
    if (allUnmappedMeasurements.length > 0) {
      fetchServerClusters();
    } else {
      setLoadingClusters(false);
    }
  }, [allUnmappedMeasurements]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        // Get organization
        const { data: orgMember } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();
        
        if (!orgMember) return;
        setOrganizationId(orgMember.organization_id);
        
        // Get unmapped measurements
        const { data: allMeasurements } = await supabase.from('measurements').select('*');
        const { data: mappings } = await supabase.from('project_mapping').select('measurement_id');
        const mappedIds = new Set(mappings?.map(m => m.measurement_id) || []);
        const unmapped = allMeasurements?.filter(m => !mappedIds.has(m.id) && m.GpsCoordinates) || [];
        setAllUnmappedMeasurements(unmapped);
        
        // Get projects with center coordinates
        const { data: projectsData } = await supabase
          .from('projects')
          .select('*')
          .eq('organization_id', orgMember.organization_id);
        
        if (projectsData) {
          const projectsWithData = await Promise.all(projectsData.map(async (project) => {
            // Get project polygon data
            const { data: geoData } = await supabase.rpc('get_project_geofence', { p_project_id: project.id });
            if (geoData) {
              try {
                const geoJson = typeof geoData === 'string' ? JSON.parse(geoData) : geoData;
                if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
                  project.parsedPolygon = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
                }
              } catch (err) {
                console.error('Error parsing GeoJSON for project', project.id, err);
              }
            }
            
            // Get project center coordinates
            const { data: centerData } = await supabase.rpc('get_project_geofence_center', { p_project_id: project.id });
            if (centerData) {
              try {
                const centerJson = typeof centerData === 'string' ? JSON.parse(centerData) : centerData;
                if (centerJson?.coordinates) {
                  project.centerCoord = { lat: centerJson.coordinates[1], lng: centerJson.coordinates[0] };
                }
              } catch (err) {
                console.error('Error parsing center:', err);
              }
            }
            return project;
          }));
          setProjects(projectsWithData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoadingMeasurements(false);
      }
    };
    
    fetchData();
  }, [user]);

  // Handlers
  const handleSelectMeasurement = (measurementId, checked) => {
    setSelectedMeasurements(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(measurementId);
      } else {
        newSet.delete(measurementId);
      }
      return newSet;
    });
  };

  const handleSelectAllForProject = (projectId, checked) => {
    const projectGroup = nearbyByProject.get(projectId);
    if (!projectGroup) return;
    
    setSelectedMeasurements(prev => {
      const newSet = new Set(prev);
      projectGroup.measurements.forEach(m => {
        if (checked) {
          newSet.add(m.id);
        } else {
          newSet.delete(m.id);
        }
      });
      return newSet;
    });
  };

  const handleBulkAssignToProject = async (projectId, specificMeasurements = null) => {
    let measurementsToAssign;
    
    if (specificMeasurements) {
      // Direct assignment from smart grouping suggestion
      measurementsToAssign = specificMeasurements;
    } else {
      // Assignment from selected measurements
      const selectedIds = Array.from(selectedMeasurements);
      measurementsToAssign = currentMeasurements.filter(m => 
        selectedIds.includes(m.id)
      );
    }
    
    if (measurementsToAssign.length === 0) return;
    
    const project = measurementsToAssign[0].closestProject;
    if (!project || project.id !== projectId) return;
    
    setLoading(true);
    try {
      // Insert all mappings
      const mappingInserts = measurementsToAssign.map(m => ({
        project_id: project.id,
        measurement_id: m.id,
        mapped_at: new Date()
      }));
      
      const { error } = await supabase
        .from('project_mapping')
        .insert(mappingInserts);
      
      if (!error) {
        await supabase.rpc('update_project_geofence', { p_project_id: project.id });
        window.location.reload();
      }
    } catch (error) {
      console.error('Error bulk assigning measurements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToProject = async (measurement, project) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('project_mapping')
        .insert([{
          project_id: project.id,
          measurement_id: measurement.id,
          mapped_at: new Date()
        }]);
      
      if (!error) {
        await supabase.rpc('update_project_geofence', { p_project_id: project.id });
        // Refresh data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error assigning measurement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim() || !newProjectModal.measurement) return;
    
    setLoading(true);
    try {
      const coords = newProjectModal.measurement.GpsCoordinates.split(',').map(Number);
      const masterGpsWKT = `SRID=4326;POINT(${coords[1]} ${coords[0]})`;
      
      const { data: newProjectData, error: projectError } = await supabase
        .from('projects')
        .insert([{
          name: newProjectName,
          master_gps: masterGpsWKT,
          organization_id: organizationId,
          description: 'Project created from individual measurement'
        }])
        .select();
      
      if (projectError) throw projectError;
      
      const newProject = newProjectData[0];
      
      await supabase
        .from('project_mapping')
        .insert([{
          project_id: newProject.id,
          measurement_id: newProjectModal.measurement.id,
          mapped_at: new Date()
        }]);
      
      await supabase.rpc('update_project_geofence', { p_project_id: newProject.id });
      
      setNewProjectModal({ isOpen: false, measurement: null });
      setNewProjectName('');
      window.location.reload();
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    setLoading(true);
    try {
      const unmappedIds = allUnmappedMeasurements.map(m => m.id);
      const { error } = await supabase.from('measurements').delete().in('id', unmappedIds);
      
      if (!error) {
        setAllUnmappedMeasurements([]);
        setDeleteModalOpen(false);
      }
    } catch (error) {
      console.error('Error deleting measurements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFocusOnMeasurement = (measurement) => {
    setFocusedMeasurement(measurement);
    setMapPopupOpen(true);
  };

  if (loadingMeasurements || loadingClusters) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <ArrowPathIcon className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">
            {loadingMeasurements ? 'Loading measurements...' : 'Analyzing clusters...'}
          </span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex flex-col">
          <h1 className="text-xl font-semibold text-gray-900">Unmapped Measurements</h1>
          <p className="text-sm text-gray-600 mt-1">
            Organize measurements into projects using Smart Map for clusters or individual assignment.
          </p>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setDeleteModalOpen(true)}
            disabled={allUnmappedMeasurements.length === 0}
            className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md shadow-sm
              ${allUnmappedMeasurements.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
              }`}
          >
            <ExclamationCircleIcon className="h-4 w-4 mr-2" />
            Delete All ({allUnmappedMeasurements.length})
          </button>
        </div>
      </div>

      {/* Smart Map Notification Card */}
      {clusterCount > 0 && (
        <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-11 h-11 bg-yellow-500 rounded-lg flex items-center justify-center">
                  <BoltIcon className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-yellow-900">
                  {clusterCount} Measurement Clusters Detected
                </h3>
                <p className="text-sm text-yellow-700">
                  We found {clusteredCount} measurements grouped into {clusterCount} clusters. 
                  Use Smart Map to create projects from these clusters efficiently.
                </p>
                
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={() => setSmartMapModalOpen(true)}
                className="inline-flex items-center px-3 py-2 bg-yellow-600 text-white font-medium rounded-lg hover:bg-yellow-700 shadow-sm"
              >
                <BoltIcon className="h-4 w-4 mr-2" />
                Smart Map
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Grouping Suggestions for Nearby Measurements */}
      {viewMode === 'nearby' && nearbyByProject.size > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Smart Grouping Suggestions</h3>
          <div className="space-y-3">
            {Array.from(nearbyByProject.entries())
              .filter(([projectId, group]) => group.measurements.length > 1)
              .map(([projectId, group]) => (
              <div key={projectId} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <ArrowsPointingOutIcon className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <div className="font-medium text-blue-900">
                        {group.measurements.length} measurements near "{group.project.name}"
                      </div>
                      <div className="text-sm text-blue-600">
                        Consider adding all {group.measurements.length} measurements to this project at once
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleSelectAllForProject(projectId, true)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-md hover:bg-blue-200"
                    >
                      Select All {group.measurements.length}
                    </button>
                    <button
                      onClick={() => handleBulkAssignToProject(projectId, group.measurements)}
                      disabled={loading}
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Add All to Project
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedMeasurements.size > 0 && (
        <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-sm font-medium text-yellow-800">
                {selectedMeasurements.size} measurement{selectedMeasurements.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSelectedMeasurements(new Set())}
                className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200"
              >
                Clear Selection
              </button>
              {viewMode === 'nearby' && (() => {
                // Find the most common project among selected measurements
                const selectedMeass = currentMeasurements.filter(m => selectedMeasurements.has(m.id));
                const projectCounts = new Map();
                selectedMeass.forEach(m => {
                  if (m.closestProject) {
                    const id = m.closestProject.id;
                    projectCounts.set(id, (projectCounts.get(id) || 0) + 1);
                  }
                });
                const [mostCommonProjectId, count] = [...projectCounts.entries()]
                  .reduce((max, curr) => curr[1] > max[1] ? curr : max, [null, 0]);
                
                if (mostCommonProjectId && count > 0) {
                  const project = selectedMeass.find(m => m.closestProject?.id === mostCommonProjectId)?.closestProject;
                  return (
                    <button
                      onClick={() => handleBulkAssignToProject(mostCommonProjectId)}
                      disabled={loading}
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Add {count} to "{project?.name}"
                    </button>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* View Toggle and Map Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('nearby')}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'nearby'
                ? 'bg-white text-green-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Near Projects ({nearbyMeasurements.length})
          </button>
          <button
            onClick={() => setViewMode('isolated')}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'isolated'
                ? 'bg-white text-red-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Isolated ({isolatedMeasurements.length})
          </button>
        </div>

        
      </div>

      {/* Individual Measurements Table */}
      {currentMeasurements.length > 0 ? (
        <div className="mb-8">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                {viewMode === 'nearby' ? 'Measurements Near Projects' : 'Isolated Measurements'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {viewMode === 'nearby' 
                  ? 'Measurements within 3km of existing projects'
                  : 'Measurements far from existing projects'
                }
              </p>
            </div>
            
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={paginatedMeasurements.length > 0 && paginatedMeasurements.every(m => selectedMeasurements.has(m.id))}
                      onChange={(e) => {
                        const allSelected = e.target.checked;
                        setSelectedMeasurements(prev => {
                          const newSet = new Set(prev);
                          paginatedMeasurements.forEach(m => {
                            if (allSelected) {
                              newSet.add(m.id);
                            } else {
                              newSet.delete(m.id);
                            }
                          });
                          return newSet;
                        });
                      }}
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Results
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {viewMode === 'nearby' ? 'Closest Project' : 'Status'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedMeasurements.map((measurement) => (
                  <tr key={measurement.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300"
                        checked={selectedMeasurements.has(measurement.id)}
                        onChange={(e) => handleSelectMeasurement(measurement.id, e.target.checked)}
                      />
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="font-medium text-gray-900">
                        {measurement.projectdescription || 'No Description from Gauge'}
                      </div>
                      {viewMode === 'nearby' && measurement.closestProject && 
                        nearbyByProject.get(measurement.closestProject.id)?.measurements.length > 1 && (
                        <div className="text-xs text-blue-600 mt-1">
                          Part of {nearbyByProject.get(measurement.closestProject.id).measurements.length} near {measurement.closestProject.name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      <div className="space-y-1">
                        <div>Density: <span className="font-medium">{measurement.Density?.toFixed(1)}</span></div>
                        <div>Compaction: <span className="font-medium">{measurement.Compaction?.toFixed(1)}</span></div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(measurement.measurement_created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {viewMode === 'nearby' && measurement.closestProject ? (
                        <div className="flex items-center">
                          <ArrowsPointingOutIcon className="h-4 w-4 text-blue-500 mr-2" />
                          <div>
                            <div className="font-medium text-blue-900">{measurement.closestProject.name}</div>
                            <div className="text-xs text-blue-600">
                              {measurement.distanceToClosest === 0 
                                ? 'Inside project boundary'
                                : `${(measurement.distanceToClosest / 1000).toFixed(1)}km away`
                              }
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">
                          {viewMode === 'isolated' ? 'No nearby projects' : 'Not assigned'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleFocusOnMeasurement(measurement)}
                          className="px-3 py-2 bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200"
                          title="View on map"
                        >
                          <MapPinIcon className="h-4 w-4" />
                        </button>
                        {viewMode === 'nearby' && measurement.closestProject && (
                          <button
                            onClick={() => handleAssignToProject(measurement, measurement.closestProject)}
                            disabled={loading}
                            className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-md hover:bg-blue-200 disabled:opacity-50"
                            title={`Add to ${measurement.closestProject.name}`}
                          >
                            {measurement.distanceToClosest === 0 ? 'Assign' : 'Add'}
                          </button>
                        )}
                        <button
                          onClick={() => setNewProjectModal({ isOpen: true, measurement })}
                          className="px-3 py-1 bg-green-100 text-green-700 text-xs rounded-md hover:bg-green-200"
                        >
                          Create New
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing {startIdx + 1} to {Math.min(startIdx + measurementsPerPage, currentMeasurements.length)} of {currentMeasurements.length} measurements
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 text-sm text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
            {viewMode === 'nearby' ? (
              <ArrowsPointingOutIcon className="h-6 w-6 text-gray-400" />
            ) : (
              <ExclamationCircleIcon className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No {viewMode === 'nearby' ? 'nearby' : 'isolated'} measurements
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {viewMode === 'nearby' 
              ? 'All individual measurements are either clustered or isolated.'
              : 'All individual measurements are either clustered or near existing projects.'
            }
          </p>
        </div>
      )}

      {/* Empty State */}
      {allUnmappedMeasurements.length === 0 && (
        <div className="text-center py-12">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">All measurements organized!</h3>
          <p className="text-gray-500">No unmapped measurements found. Great work!</p>
        </div>
      )}

      {/* Smart Map Modal */}
      {smartMapModalOpen && (
        <SmartMapModal
          clusters={serverClusters}
          onClose={() => setSmartMapModalOpen(false)}
          onProjectCreated={() => {
            setSmartMapModalOpen(false);
            window.location.reload();
          }}
        />
      )}

      {/* Map Popup */}
      <MapPopup
        isOpen={mapPopupOpen}
        onClose={() => {
          setMapPopupOpen(false);
          setFocusedMeasurement(null);
        }}
        measurements={currentMeasurements}
        projects={viewMode === 'nearby' ? projects : []}
        title={`${viewMode === 'nearby' ? 'Measurements Near Projects' : 'Isolated Measurements'} (${currentMeasurements.length})`}
        focusedMeasurement={focusedMeasurement}
      />

      {/* New Project Modal */}
      {newProjectModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Create New Project</h3>
            <p className="text-sm text-gray-600 mb-4">
              Create a new project with this individual measurement.
            </p>
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              placeholder="Enter project name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setNewProjectModal({ isOpen: false, measurement: null });
                  setNewProjectName('');
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                disabled={!newProjectName.trim() || loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <ExclamationCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-center text-gray-900 mb-4">
              Delete All Unmapped Measurements
            </h3>
            <p className="text-sm text-gray-500 mb-6 text-center">
              Are you sure? This will delete all {allUnmappedMeasurements.length} unmapped measurements. This action cannot be undone.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}